import React, { useEffect, useState } from 'react';
import { statsApi, transactionApi } from '../services/api';
import { OverallStats, Transaction, MonthlyStats, DailyStats } from '../types';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, AreaChart, Area, ReferenceLine } from 'recharts';
import './Dashboard.css';

const Dashboard: React.FC = () => {
  const [overallStats, setOverallStats] = useState<OverallStats | null>(null);
  const [monthlyStats, setMonthlyStats] = useState<MonthlyStats[]>([]);
  const [dailyStats, setDailyStats] = useState<DailyStats[]>([]);
  const [monthTransactions, setMonthTransactions] = useState<Transaction[]>([]);
  const [yearTransactions, setYearTransactions] = useState<Transaction[]>([]);
  const [recentTransactions, setRecentTransactions] = useState<Transaction[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState<number>(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());

  const monthNames = [
    'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
    'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
  ];

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const currentYear = new Date().getFullYear();
        const [overall, monthly, transactions, yearTx] = await Promise.all([
          statsApi.getOverall(),
          statsApi.getMonthly(),
          transactionApi.getAll(),
          transactionApi.getAll({
            startDate: `${currentYear}-01-01`,
            endDate: `${currentYear}-12-31`
          })
        ]);

        setOverallStats(overall.data);
        setMonthlyStats(monthly.data);
        setRecentTransactions(transactions.data.slice(0, 5));
        setYearTransactions(Array.isArray(yearTx.data) ? yearTx.data : []);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchInitialData();
  }, []);

  useEffect(() => {
    const fetchMonthData = async () => {
      const pad = (n: number) => String(n).padStart(2, '0');
      const lastDay = new Date(selectedYear, selectedMonth, 0).getDate();
      const startDate = `${selectedYear}-${pad(selectedMonth)}-01`;
      const endDate = `${selectedYear}-${pad(selectedMonth)}-${pad(lastDay)}`;
      try {
        const [daily, monthTx] = await Promise.all([
          statsApi.getDaily(selectedYear, selectedMonth),
          transactionApi.getAll({ startDate, endDate })
        ]);
        setDailyStats(Array.isArray(daily.data) ? daily.data : []);
        setMonthTransactions(Array.isArray(monthTx.data) ? monthTx.data : []);
      } catch (error) {
        console.error('Error fetching monthly data:', error);
        setDailyStats([]);
        setMonthTransactions([]);
      }
    };

    fetchMonthData();
  }, [selectedYear, selectedMonth]);

  const handlePrevMonth = () => {
    if (selectedMonth === 1) {
      setSelectedMonth(12);
      setSelectedYear(selectedYear - 1);
    } else {
      setSelectedMonth(selectedMonth - 1);
    }
  };

  const handleNextMonth = () => {
    const now = new Date();
    const isCurrentMonth = selectedYear === now.getFullYear() && selectedMonth === now.getMonth() + 1;

    if (!isCurrentMonth) {
      if (selectedMonth === 12) {
        setSelectedMonth(1);
        setSelectedYear(selectedYear + 1);
      } else {
        setSelectedMonth(selectedMonth + 1);
      }
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const formatCompactCurrency = (amount: number) => {
    if (Math.abs(amount) >= 1000) {
      return (amount / 1000).toFixed(1) + 'k €';
    }
    return amount.toFixed(0) + ' €';
  };

  const monthNamesShort = [
    'Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun',
    'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'
  ];

  const chartData = monthlyStats.map(stat => ({
    name: monthNamesShort[stat.month - 1],
    Einzahlungen: stat.deposits,
    Auszahlungen: stat.withdrawals,
    Bilanz: stat.balance
  }));

  // Bilanz pro Casino für den gewählten Monat (Auszahlung - Einzahlung).
  const casinoBalances = React.useMemo(() => {
    const map = new Map<number, { name: string; logo: string | null; balance: number }>();
    monthTransactions.forEach(t => {
      const entry = map.get(t.casino_id) || {
        name: t.casino_name,
        logo: t.casino_logo,
        balance: 0
      };
      entry.balance += t.type === 'withdrawal' ? Number(t.amount) : -Number(t.amount);
      map.set(t.casino_id, entry);
    });
    return Array.from(map.values());
  }, [monthTransactions]);

  const topWinners = casinoBalances
    .filter(c => c.balance > 0)
    .sort((a, b) => b.balance - a.balance)
    .slice(0, 5);

  const topLosers = casinoBalances
    .filter(c => c.balance < 0)
    .sort((a, b) => a.balance - b.balance)
    .slice(0, 5);

  // Bester/schlechtester Monat des aktuellen Jahres (nach Bilanz).
  const monthsWithActivity = monthlyStats.filter(s => s.deposits > 0 || s.withdrawals > 0);
  const bestMonth = monthsWithActivity.length
    ? monthsWithActivity.reduce((a, b) => (b.balance > a.balance ? b : a))
    : null;
  const worstMonth = monthsWithActivity.length
    ? monthsWithActivity.reduce((a, b) => (b.balance < a.balance ? b : a))
    : null;

  // Bestes/schlechtestes Casino des Jahres (Bilanz = Auszahlung - Einzahlung).
  const yearCasinoBalances = React.useMemo(() => {
    const map = new Map<number, { name: string; logo: string | null; balance: number }>();
    yearTransactions.forEach(t => {
      const entry = map.get(t.casino_id) || { name: t.casino_name, logo: t.casino_logo, balance: 0 };
      entry.balance += t.type === 'withdrawal' ? Number(t.amount) : -Number(t.amount);
      map.set(t.casino_id, entry);
    });
    return Array.from(map.values());
  }, [yearTransactions]);

  const bestCasino = yearCasinoBalances.length
    ? yearCasinoBalances.reduce((a, b) => (b.balance > a.balance ? b : a))
    : null;
  const worstCasino = yearCasinoBalances.length
    ? yearCasinoBalances.reduce((a, b) => (b.balance < a.balance ? b : a))
    : null;

  const dailyChartData = dailyStats.map(stat => ({
    name: stat.day.toString(),
    Bilanz: stat.cumulative_balance
  }));

  const currentMonthBalance = dailyStats.length > 0
    ? dailyStats[dailyStats.length - 1].cumulative_balance
    : 0;

  // Dynamische Chart-Farbe basierend auf der Monatsbilanz (für Rahmen/Tooltip)
  const chartColor = currentMonthBalance >= 0 ? '#4caf50' : '#ff5252';

  // Offset des Nulldurchgangs für den Split-Gradient (grün über 0, rot unter 0).
  // Recharts-Gradient verläuft von oben (max) nach unten (min); der Anteil
  // oberhalb von 0 ergibt den Umschlagpunkt.
  const balanceValues = dailyChartData.map(d => d.Bilanz);
  const maxBalance = balanceValues.length ? Math.max(...balanceValues, 0) : 0;
  const minBalance = balanceValues.length ? Math.min(...balanceValues, 0) : 0;
  const zeroOffset = maxBalance === minBalance
    ? (maxBalance >= 0 ? 0 : 1)
    : maxBalance / (maxBalance - minBalance);
  const POS = '#4caf50';
  const NEG = '#ff5252';

  // Berechne Trend (Vergleich zum Vormonat)
  const currentMonthStats = monthlyStats.find(s => s.month === new Date().getMonth() + 1);
  const prevMonthStats = monthlyStats.find(s => s.month === new Date().getMonth());
  const balanceTrend = currentMonthStats && prevMonthStats
    ? currentMonthStats.balance - prevMonthStats.balance
    : 0;

  if (isLoading) {
    return (
      <div className="dashboard-loading">
        <div className="loading-spinner"></div>
        <p>Lade Dashboard...</p>
      </div>
    );
  }

  return (
    <div className="dashboard">
      {/* Header Section */}
      <div className="dashboard-header">
        <div className="header-content">
          <h1>Dashboard</h1>
          <p className="header-subtitle">Willkommen zurück! Hier ist deine Übersicht.</p>
        </div>
        <div className="header-date">
          {new Date().toLocaleDateString('de-DE', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
        </div>
      </div>

      {/* Main Stats Cards */}
      <div className="stats-grid">
        {/* Gesamtbilanz - Große Karte */}
        <div className={`stat-card main-stat ${(overallStats?.balance ?? 0) >= 0 ? 'positive-card' : 'negative-card'}`}>
          <div className="stat-card-inner">
            <div className="stat-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
              </svg>
            </div>
            <div className="stat-content">
              <span className="stat-label">Gesamtbilanz</span>
              <span className={`stat-value-large ${(overallStats?.balance ?? 0) >= 0 ? 'positive' : 'negative'}`}>
                {formatCurrency(overallStats?.balance ?? 0)}
              </span>
              {balanceTrend !== 0 && (
                <span className={`stat-trend ${balanceTrend >= 0 ? 'trend-up' : 'trend-down'}`}>
                  {balanceTrend >= 0 ? '↑' : '↓'} {formatCurrency(Math.abs(balanceTrend))} vs. Vormonat
                </span>
              )}
            </div>
          </div>
          <div className="stat-card-bg"></div>
        </div>

        {/* Einzahlungen */}
        <div className="stat-card">
          <div className="stat-card-inner">
            <div className="stat-icon deposit-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 19V5M5 12l7-7 7 7" />
              </svg>
            </div>
            <div className="stat-content">
              <span className="stat-label">Einzahlungen</span>
              <span className="stat-value negative">{formatCurrency(overallStats?.total_deposits ?? 0)}</span>
            </div>
          </div>
        </div>

        {/* Auszahlungen */}
        <div className="stat-card">
          <div className="stat-card-inner">
            <div className="stat-icon withdrawal-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 5v14M5 12l7 7 7-7" />
              </svg>
            </div>
            <div className="stat-content">
              <span className="stat-label">Auszahlungen</span>
              <span className="stat-value positive">{formatCurrency(overallStats?.total_withdrawals ?? 0)}</span>
            </div>
          </div>
        </div>

        {/* Transaktionen */}
        <div className="stat-card">
          <div className="stat-card-inner">
            <div className="stat-icon transaction-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <div className="stat-content">
              <span className="stat-label">Transaktionen</span>
              <span className="stat-value">{overallStats?.total_transactions ?? 0}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Tagesbilanz Chart */}
      <div className="chart-section daily-section">
        <div className="section-header">
          <div className="section-title">
            <h2>Tagesverlauf</h2>
            <p className="section-subtitle">Kumulative Bilanz für den ausgewählten Monat</p>
          </div>
          <div className="month-selector">
            <button className="month-nav-btn" onClick={handlePrevMonth}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M15 18l-6-6 6-6" />
              </svg>
            </button>
            <span className="month-display">{monthNames[selectedMonth - 1]} {selectedYear}</span>
            <button
              className="month-nav-btn"
              onClick={handleNextMonth}
              disabled={selectedYear === new Date().getFullYear() && selectedMonth === new Date().getMonth() + 1}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 18l6-6-6-6" />
              </svg>
            </button>
          </div>
        </div>

        <div className="month-balance-display">
          <span className="month-balance-label">Monatsbilanz</span>
          <span className={`month-balance-value ${currentMonthBalance >= 0 ? 'positive' : 'negative'}`}>
            {currentMonthBalance >= 0 ? '+' : ''}{formatCurrency(currentMonthBalance)}
          </span>
        </div>

        <div className="chart-container-modern">
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={dailyChartData}>
              <defs>
                {/* Füllung: grün oberhalb von 0, rot unterhalb – Umschlag am Nullpunkt */}
                <linearGradient id="fillBilanz" x1="0" y1="0" x2="0" y2="1">
                  <stop offset={0} stopColor={POS} stopOpacity={0.35} />
                  <stop offset={zeroOffset} stopColor={POS} stopOpacity={0.08} />
                  <stop offset={zeroOffset} stopColor={NEG} stopOpacity={0.08} />
                  <stop offset={1} stopColor={NEG} stopOpacity={0.35} />
                </linearGradient>
                {/* Linie: harter Umschlag exakt am Nullpunkt */}
                <linearGradient id="strokeBilanz" x1="0" y1="0" x2="0" y2="1">
                  <stop offset={0} stopColor={POS} />
                  <stop offset={zeroOffset} stopColor={POS} />
                  <stop offset={zeroOffset} stopColor={NEG} />
                  <stop offset={1} stopColor={NEG} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis
                dataKey="name"
                stroke="#666"
                tick={{ fill: '#888', fontSize: 11 }}
                axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
              />
              <YAxis
                stroke="#666"
                tick={{ fill: '#888', fontSize: 11 }}
                tickFormatter={(v) => formatCompactCurrency(v)}
                axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'rgba(26, 26, 46, 0.95)',
                  border: `1px solid ${currentMonthBalance >= 0 ? 'rgba(76, 175, 80, 0.3)' : 'rgba(255, 82, 82, 0.3)'}`,
                  borderRadius: '8px',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
                }}
                formatter={(value: number) => [formatCurrency(value), 'Bilanz']}
                labelFormatter={(label) => `Tag ${label}`}
              />
              <ReferenceLine y={0} stroke="rgba(255,255,255,0.25)" strokeDasharray="2 2" />
              <Area
                type="monotone"
                dataKey="Bilanz"
                stroke="url(#strokeBilanz)"
                strokeWidth={3}
                fill="url(#fillBilanz)"
                dot={(props: any) => {
                  const { cx, cy, payload, key } = props;
                  const color = payload.Bilanz >= 0 ? POS : NEG;
                  return (
                    <circle
                      key={key}
                      cx={cx}
                      cy={cy}
                      r={3}
                      fill={color}
                      stroke={color}
                      strokeWidth={2}
                    />
                  );
                }}
                activeDot={(props: any) => {
                  const { cx, cy, payload, key } = props;
                  const color = payload.Bilanz >= 0 ? POS : NEG;
                  return (
                    <circle
                      key={key}
                      cx={cx}
                      cy={cy}
                      r={6}
                      fill={color}
                      stroke="#fff"
                      strokeWidth={2}
                    />
                  );
                }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Tägliche Übersicht + Top Casinos */}
      <div className="daily-overview-grid">
      <div className="chart-section">
        <div className="section-header">
          <div className="section-title">
            <h2>Tagesübersicht {monthNames[selectedMonth - 1]} {selectedYear}</h2>
            <p className="section-subtitle">Einzahlungen, Auszahlungen und Bilanz pro Tag</p>
          </div>
        </div>
        <div className="daily-table-container">
          <table className="daily-table daily-table--compact">
            <thead>
              <tr>
                <th>Tag</th>
                <th>Einzahlung</th>
                <th>Auszahlung</th>
                <th>Bilanz</th>
              </tr>
            </thead>
            <tbody>
              {dailyStats.filter(day => day.deposits > 0 || day.withdrawals > 0).length === 0 ? (
                <tr>
                  <td colSpan={4} className="no-data-cell">Keine Transaktionen in diesem Monat</td>
                </tr>
              ) : (
                dailyStats
                  .filter(day => day.deposits > 0 || day.withdrawals > 0)
                  .map(day => (
                    <tr key={day.day}>
                      <td className="day-cell">{day.day}. {monthNames[selectedMonth - 1].slice(0, 3)}</td>
                      <td className={day.deposits > 0 ? 'negative' : ''}>{day.deposits > 0 ? formatCurrency(day.deposits) : '-'}</td>
                      <td className={day.withdrawals > 0 ? 'positive' : ''}>{day.withdrawals > 0 ? formatCurrency(day.withdrawals) : '-'}</td>
                      <td className={day.balance >= 0 ? 'positive' : 'negative'}>
                        {day.balance >= 0 ? '+' : ''}{formatCurrency(day.balance)}
                      </td>
                    </tr>
                  ))
              )}
            </tbody>
            {dailyStats.filter(day => day.deposits > 0 || day.withdrawals > 0).length > 0 && (
              <tfoot>
                <tr className="total-row">
                  <td><strong>Gesamt</strong></td>
                  <td className="negative"><strong>{formatCurrency(dailyStats.reduce((sum, d) => sum + d.deposits, 0))}</strong></td>
                  <td className="positive"><strong>{formatCurrency(dailyStats.reduce((sum, d) => sum + d.withdrawals, 0))}</strong></td>
                  <td className={currentMonthBalance >= 0 ? 'positive' : 'negative'}>
                    <strong>{currentMonthBalance >= 0 ? '+' : ''}{formatCurrency(currentMonthBalance)}</strong>
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {/* Top Casinos des Monats */}
      <div className="top-casinos-column">
        <div className="top-casino-card">
          <div className="top-casino-header">
            <h3>Top 5 Gewinner</h3>
            <span className="top-casino-sub">{monthNames[selectedMonth - 1]}</span>
          </div>
          <div className="top-casino-list">
            {topWinners.length === 0 ? (
              <p className="no-data-cell">Keine Gewinner</p>
            ) : (
              topWinners.map((c, i) => (
                <div key={c.name + i} className="top-casino-item">
                  <span className="top-casino-rank">{i + 1}</span>
                  <span className="top-casino-logo">
                    {c.logo ? (
                      <img
                        src={c.logo}
                        alt={c.name}
                        onError={(e) => {
                          const img = e.target as HTMLImageElement;
                          img.style.display = 'none';
                          img.parentElement?.classList.add('tcl-fallback');
                        }}
                      />
                    ) : (
                      <span className="tcl-initials">{c.name.slice(0, 2).toUpperCase()}</span>
                    )}
                  </span>
                  <span className="top-casino-name">{c.name}</span>
                  <span className="top-casino-value positive">+{formatCurrency(c.balance)}</span>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="top-casino-card">
          <div className="top-casino-header">
            <h3>Top 5 Verlierer</h3>
            <span className="top-casino-sub">{monthNames[selectedMonth - 1]}</span>
          </div>
          <div className="top-casino-list">
            {topLosers.length === 0 ? (
              <p className="no-data-cell">Keine Verlierer</p>
            ) : (
              topLosers.map((c, i) => (
                <div key={c.name + i} className="top-casino-item">
                  <span className="top-casino-rank">{i + 1}</span>
                  <span className="top-casino-logo">
                    {c.logo ? (
                      <img
                        src={c.logo}
                        alt={c.name}
                        onError={(e) => {
                          const img = e.target as HTMLImageElement;
                          img.style.display = 'none';
                          img.parentElement?.classList.add('tcl-fallback');
                        }}
                      />
                    ) : (
                      <span className="tcl-initials">{c.name.slice(0, 2).toUpperCase()}</span>
                    )}
                  </span>
                  <span className="top-casino-name">{c.name}</span>
                  <span className="top-casino-value negative">{formatCurrency(c.balance)}</span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
      </div>

      {/* Bottom Grid */}
      <div className="dashboard-bottom-grid">
        {/* Monatliche Übersicht */}
        <div className="chart-section">
          <div className="section-header">
            <div className="section-title">
              <h2>Jahresübersicht {new Date().getFullYear()}</h2>
              <p className="section-subtitle">Einzahlungen vs. Auszahlungen pro Monat</p>
            </div>
          </div>
          <div className="chart-container-modern">
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={chartData} barGap={2}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis
                  dataKey="name"
                  stroke="#666"
                  tick={{ fill: '#888', fontSize: 11 }}
                  axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
                />
                <YAxis
                  stroke="#666"
                  tick={{ fill: '#888', fontSize: 11 }}
                  tickFormatter={(v) => formatCompactCurrency(v)}
                  axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'rgba(26, 26, 46, 0.95)',
                    border: '1px solid rgba(255, 215, 0, 0.3)',
                    borderRadius: '8px',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
                  }}
                  formatter={(value: number) => formatCurrency(value)}
                />
                <Legend
                  wrapperStyle={{ paddingTop: '10px' }}
                  formatter={(value) => <span style={{ color: '#888' }}>{value}</span>}
                />
                <Bar dataKey="Einzahlungen" fill="#ff5252" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Auszahlungen" fill="#4caf50" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="year-highlights">
            <div className="year-highlight">
              <span className="yh-label">Bester Monat</span>
              {bestMonth ? (
                <>
                  <span className="yh-main">{monthNames[bestMonth.month - 1]}</span>
                  <span className="yh-value positive">+{formatCurrency(bestMonth.balance)}</span>
                </>
              ) : <span className="yh-main">–</span>}
            </div>

            <div className="year-highlight">
              <span className="yh-label">Schlechtester Monat</span>
              {worstMonth ? (
                <>
                  <span className="yh-main">{monthNames[worstMonth.month - 1]}</span>
                  <span className="yh-value negative">{formatCurrency(worstMonth.balance)}</span>
                </>
              ) : <span className="yh-main">–</span>}
            </div>

            <div className="year-highlight">
              <span className="yh-label">Bestes Casino</span>
              {bestCasino ? (
                <div className="yh-casino">
                  <span className="yh-logo">
                    {bestCasino.logo ? (
                      <img src={bestCasino.logo} alt={bestCasino.name}
                        onError={(e) => {
                          const img = e.target as HTMLImageElement;
                          img.style.display = 'none';
                          img.parentElement?.classList.add('tcl-fallback');
                        }} />
                    ) : (
                      <span className="tcl-initials">{bestCasino.name.slice(0, 2).toUpperCase()}</span>
                    )}
                  </span>
                  <div className="yh-casino-text">
                    <span className="yh-main">{bestCasino.name}</span>
                    <span className="yh-value positive">+{formatCurrency(bestCasino.balance)}</span>
                  </div>
                </div>
              ) : <span className="yh-main">–</span>}
            </div>

            <div className="year-highlight">
              <span className="yh-label">Schlechtestes Casino</span>
              {worstCasino ? (
                <div className="yh-casino">
                  <span className="yh-logo">
                    {worstCasino.logo ? (
                      <img src={worstCasino.logo} alt={worstCasino.name}
                        onError={(e) => {
                          const img = e.target as HTMLImageElement;
                          img.style.display = 'none';
                          img.parentElement?.classList.add('tcl-fallback');
                        }} />
                    ) : (
                      <span className="tcl-initials">{worstCasino.name.slice(0, 2).toUpperCase()}</span>
                    )}
                  </span>
                  <div className="yh-casino-text">
                    <span className="yh-main">{worstCasino.name}</span>
                    <span className="yh-value negative">{formatCurrency(worstCasino.balance)}</span>
                  </div>
                </div>
              ) : <span className="yh-main">–</span>}
            </div>
          </div>
        </div>

        {/* Letzte Transaktionen */}
        <div className="transactions-section">
          <div className="section-header">
            <div className="section-title">
              <h2>Letzte Aktivitäten</h2>
              <p className="section-subtitle">Deine neuesten Transaktionen</p>
            </div>
          </div>
          <div className="transactions-list">
            {recentTransactions.length === 0 ? (
              <div className="no-transactions">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                <p>Keine Transaktionen vorhanden</p>
              </div>
            ) : (
              recentTransactions.map((transaction, index) => (
                <div
                  key={transaction.id}
                  className="transaction-item"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className={`transaction-icon-wrapper ${transaction.type}`}>
                    {transaction.type === 'withdrawal' ? (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 5v14M5 12l7 7 7-7" />
                      </svg>
                    ) : (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 19V5M5 12l7-7 7 7" />
                      </svg>
                    )}
                  </div>
                  <div className="transaction-details">
                    <span className="transaction-casino">{transaction.casino_name}</span>
                    <span className="transaction-date">
                      {new Date(transaction.transaction_date).toLocaleDateString('de-DE', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric'
                      })}
                    </span>
                  </div>
                  <span className={`transaction-amount ${transaction.type === 'withdrawal' ? 'positive' : 'negative'}`}>
                    {transaction.type === 'withdrawal' ? '+' : '-'}
                    {formatCurrency(transaction.amount)}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
